import { ReactComponent as Times } from "../../../../../assets/images/sidebar/Times.svg";
import { ReactComponent as AddFolder } from "../../../../../assets/images/icons/folder-add.svg";
import { ReactComponent as ArrowLeft } from "../../../../../assets/images/icons/arrow-left.svg";
import { ReactComponent as ArrowRight } from "../../../../../assets/images/icons/ArrowRight.svg";
import folder from "../../../../../assets/images/fileManager/Folder.svg";
import { useEffect, useRef, useState } from "react";
import Button from "../../../button/button.index";
import { ButtonSize, ButtonType } from "../../../button/button.interface";
import TextInput from "../../../textInput/textInput.index";
import { ResourceAccessEnum, ResourceType } from "../../../../../interfaces/general";
import { forbiddenCharacters, getFileManagerParent, isFolder, isFolderWithKey } from "../../../../../scripts/helpers";
import { get, getAll, post, responseValidator } from "../../../../../scripts/api";
import API from "../../../../../urls/api";
import { cutResources, getSharedObject } from "../../fileManager.function";
import { handleSetShareCutModalStatus } from "../sharedFileManager.actions";
import Dialog from "../../../dialog/dialog.index";
import { DialogSize } from "../../../dialog/dialog.interface";
import ReactTooltip from "react-tooltip";
import { toast } from "mobin-toast";
import { toastMessages } from "../../../../../scripts/toastMessages";
import { shallowEqual } from "react-redux";
import { useAppSelector } from "../../../../../redux/hooks";

export default function SharedCutModal(): JSX.Element {
    const { shareCutModalStatus, selectedResources } = useAppSelector(
        ({ fileManager: { shareCutModalStatus, selectedResources } }) => ({ shareCutModalStatus, selectedResources }),
        shallowEqual
    );
    const [createFolder, setCreateFolder] = useState<boolean>(false);
    const [folderName, setFolderName] = useState<string>("");
    const [folderNameError, setFolderNameError] = useState<string>("");
    const [resources, setResources] = useState<ResourceType[] | undefined>(undefined);
    const [parent, setParent] = useState<ResourceType | undefined>(undefined);
    const [currentResource, setCurrentResource] = useState<ResourceType | undefined>(undefined);
    const folderButtonRef = useRef<any>();
    const cutButton = useRef<any>();
    const hiddenInputRef = useRef<any>();

    useEffect(() => {
        ReactTooltip.rebuild();
        if (hiddenInputRef.current) hiddenInputRef.current.focus();
    });

    useEffect(() => {
        if (shareCutModalStatus) {
            getResources(parent);
            if (parent) {
                handleSetCurrentResource(parent);
            } else {
                setCurrentResource(undefined);
            }
        }
    }, [parent]);

    const handleSetCurrentResource = (parent: any) => {
        if (parent) {
            get(
                (data, status) => {
                    if (responseValidator(status)) {
                        setCurrentResource({ ...data.obj, parent: data.parent_hash });
                    } else {
                        handleSetShareCutModalStatus(false);
                        toast({ title: toastMessages(71), type: "error" });
                    }
                },
                API.share.getObject("v4", parent.obj_hash!)
            );
        } else {
            setCurrentResource(undefined);
        }
    };

    useEffect(() => {
        if (shareCutModalStatus) {
            getSharedObject(getFileManagerParent() || selectedResources[0].parent_hash).then((resource) => {
                setParent(resource);
            });
        }
    }, [shareCutModalStatus, selectedResources]);

    useEffect(() => {
        if (shareCutModalStatus) {
            getResources(parent);
            if (parent) {
                get(
                    (data, status) => {
                        if (responseValidator(status)) {
                            setCurrentResource({ ...data.obj, parent: data.parent_hash });
                        } else {
                            handleSetShareCutModalStatus(false);
                            toast({ title: toastMessages(70), type: "error" });
                        }
                    },
                    API.share.getObject("v4", parent.obj_hash!)
                );
            } else {
                setCurrentResource(undefined);
            }
        }
    }, [parent]);

    const getResources = (parent: any) => {
        setResources(undefined);
        if (parent) {
            getAll(
                (data, status) => {
                    if (responseValidator(status)) {
                        const temp = data.result.map((item: any) => ({
                            ...item.obj,
                            shareData: { id: item.id, access: item.access, parent: item.parent, created_at: item.created_at }
                        }));
                        setResources(temp);
                    } else {
                        toast({ title: toastMessages(69), type: "error" });
                    }
                },
                API.share.list(),
                { obj_hash: parent.obj_hash }
            );
        }
    };

    const handleCreateNewFolder = () => {
        return new Promise((resolve) => {
            if (resources) {
                if (!folderName) {
                    toast({ title: toastMessages(68), type: "error" });
                    resolve(true);
                } else if (forbiddenCharacters.some((item) => folderName.includes(item))) {
                    toast({ title: toastMessages(67), type: "error" });
                    resolve(true);
                } else {
                    post(
                        (data, status) => {
                            resolve(true);
                            if (responseValidator(status)) {
                                getResources(parent);
                                setCreateFolder(false);
                                handleResetFolderCreation();
                                toast({ title: toastMessages(66, [folderName]), type: "success" });
                            } else {
                                toast({ title: toastMessages(65), type: "error" });
                            }
                        },
                        API.fileManager.createFolder(),
                        { name: folderName, parent: currentResource?.id }
                    );
                }
            } else {
                resolve(true);
            }
        });
    };

    const handleResetFolderCreation = () => {
        setFolderName("");
        setFolderNameError("");
    };

    const handleBackClick = () => {
        if (createFolder) {
            setCreateFolder(false);
            handleResetFolderCreation();
        } else {
            if (currentResource?.parent) {
                setResources(undefined);
                getSharedObject(currentResource.parent.toString()).then((data) => {
                    setParent(data);
                });
            }
        }
    };

    return (
        <>
            <Dialog
                size={DialogSize.MD}
                status={shareCutModalStatus}
                onClose={() => {
                    handleSetShareCutModalStatus(false);
                    setCreateFolder(false);
                    handleResetFolderCreation();
                }}
                className="!overflow-hidden !max-h-[500px] !h-full"
                closeButton={false}
            >
                <div
                    className="flex flex-col justify-between h-full"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            if (createFolder) {
                                folderButtonRef.current.click();
                            } else {
                                cutButton.current.click();
                            }
                        } else if (e.key === "Escape") {
                            handleSetShareCutModalStatus(false);
                            setCreateFolder(false);
                            handleResetFolderCreation();
                        }
                    }}
                >
                    {!createFolder && <button className="h-0 w-0 absolute" ref={hiddenInputRef} />}
                    <div className="flex items-center justify-between gap-4 bg-[#FBFBFB] px-6 py-4">
                        <Button
                            onClick={handleBackClick}
                            icon={<ArrowLeft className="stroke-transparent !fill-[#A0A3BD]" />}
                            size={ButtonSize.SM}
                            className="!p-0"
                            type={ButtonType.TEXT}
                            autoFocus={false}
                        />
                        {createFolder ? (
                            <div className="flex-1 flex items-center">ساختن پوشه جدید</div>
                        ) : (
                            <>
                                <div className="truncate flex-1 w-full">
                                    <h3 className="truncate">{currentResource ? currentResource.name : "فایل‌های من"}</h3>
                                </div>
                                <Button
                                    icon={<Times />}
                                    className="!p-0"
                                    size={ButtonSize.SM}
                                    type={ButtonType.TEXT}
                                    onClick={() => handleSetShareCutModalStatus(false)}
                                    autoFocus={false}
                                />
                            </>
                        )}
                    </div>
                    <div
                        className={`flex-1 overflow-auto no-scrollbar ${!createFolder && resources && resources.filter((item) => isFolder(item)).length === 0 ? "flex items-center justify-center" : ""}`}
                    >
                        {createFolder ? (
                            <div className="relative m-4">
                                <TextInput
                                    label="نام پوشه جدید"
                                    placeholder=".نام پوشه را وارد کنید"
                                    onChange={(value) => {
                                        if (value.length > 250) setFolderNameError("نام پوشه نباید بیشتر از ۲۵۰ کاراکتر باشد.");
                                        else setFolderNameError("");
                                        setFolderName(value);
                                    }}
                                    error={folderNameError}
                                    autoFocus={true}
                                    value={folderName}
                                />
                                <div className="flex items-center absolute top-1 left-0 font-normal text-[12px]">
                                    <div className="!text-[#A0A3BD] !font-bold !text-[10px]">{"250/"}</div>
                                    {`${folderName.length}`}
                                </div>
                            </div>
                        ) : !resources ? (
                            Array.from(Array(10).keys()).map((item, index) => {
                                return (
                                    <div className="flex items-center gap-4 px-6 py-4" key={index}>
                                        <div className="skeleton w-[40px] h-[40px]" />
                                        <div className="w-[100px] h-[20px] skeleton" />
                                    </div>
                                );
                            })
                        ) : resources.filter((item) => isFolderWithKey(item.key) && item.shareData?.access === ResourceAccessEnum.WRITE).length >
                          0 ? (
                            resources
                                .filter((item) => isFolderWithKey(item.key) && item.shareData?.access === ResourceAccessEnum.WRITE)
                                .map((resource) => {
                                    return (
                                        !selectedResources.map((item: ResourceType) => item.id).includes(resource.id) && (
                                            <div
                                                className="flex items-center justify-between gap-4 px-6 py-4 cursor-pointer hover:bg-primary-50"
                                                onClick={() => setParent(resource)}
                                                key={resource.id}
                                            >
                                                <img src={folder} alt="folder" />
                                                <h3 className="flex-1 truncate">{resource.name}</h3>
                                                <ArrowRight className="rotate-180" />
                                            </div>
                                        )
                                    );
                                })
                        ) : (
                            <h4>در این پوشه هیچ پوشه‌ای وجود ندارد!</h4>
                        )}
                    </div>
                    <div className="flex items-center justify-between px-6 py-4 bg-[#FBFBFB]">
                        {!createFolder ? (
                            <Button
                                onClick={() => setCreateFolder(true)}
                                icon={<AddFolder className="stroke-transparent fill-secondary-600" />}
                                className="!p-0"
                                size={ButtonSize.SM}
                                type={ButtonType.TEXT}
                                autoFocus={false}
                            />
                        ) : (
                            <div />
                        )}
                        <div>
                            {createFolder ? (
                                <Button
                                    text="ساخت پوشه"
                                    size={ButtonSize.SM}
                                    className="!px-8"
                                    onClick={handleCreateNewFolder}
                                    disable={!(folderName && folderName.length <= 250)}
                                    autoFocus={true}
                                    ref={folderButtonRef}
                                />
                            ) : (
                                <Button
                                    text="انتقال به اینجا"
                                    size={ButtonSize.SM}
                                    className="!px-8"
                                    onClick={() => cutResources(undefined, currentResource ? currentResource.id : null)}
                                    disable={
                                        resources === undefined
                                            ? true
                                            : createFolder
                                              ? true
                                              : resources
                                                    ?.map((resource) => resource.id)
                                                    .some((item) => selectedResources.map((item: ResourceType) => item.id).includes(item))
                                    }
                                    autoFocus={true}
                                    ref={cutButton}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
